'use client';

import * as React from 'react';
import { cn } from '../../lib/utils';

export interface AvatarProps extends React.ComponentProps<'div'> {
  /** Image source URL. If absent or fails to load, the fallback initials render. */
  src?: string;
  alt?: string;
  /** Initials or short text shown when no image is available. */
  fallback?: string;
  /** Predefined size: sm = 24px, md = 32px, lg = 40px, xl = 48px. Or pass a number for custom px. */
  size?: 'sm' | 'md' | 'lg' | 'xl' | number;
}

const SIZE_MAP: Record<'sm' | 'md' | 'lg' | 'xl', number> = {
  sm: 24,
  md: 32,
  lg: 40,
  xl: 48,
};

export const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  ({ src, alt, fallback, size = 'md', className, style, ...props }, ref) => {
    const [imgErrored, setImgErrored] = React.useState(false);
    const px = typeof size === 'number' ? size : SIZE_MAP[size];
    const fontPx = Math.max(10, Math.round(px * 0.4));
    const showImage = !!src && !imgErrored;

    return (
      <div
        ref={ref}
        className={cn(
          'relative inline-flex items-center justify-center overflow-hidden rounded-full bg-muted text-muted-foreground select-none',
          className,
        )}
        style={{ width: px, height: px, fontSize: fontPx, ...style }}
        {...props}
      >
        {showImage ? (
          <img
            src={src}
            alt={alt ?? fallback ?? 'avatar'}
            className='h-full w-full object-cover'
            onError={() => setImgErrored(true)}
          />
        ) : (
          <span className='font-semibold uppercase'>{fallback?.slice(0, 2) ?? ''}</span>
        )}
      </div>
    );
  },
);
Avatar.displayName = 'Avatar';
